// camera-config.ts — the camera's details as typed, field by field
// (ENG-3796 spec.md §3b / prototype.html "Camera details").
//
// A leaf on purpose: this is imported by onboarding's retired screen AND by the
// shared Add Video Source panel, so it must not drag a model graph behind it.
// Putting it in the same file as the URL composition pulled `formatUri` and the
// video/control protocol modules into every onboarding module that touches
// `wizard/types.js`, which broke that screen's tests and slowed the suite to a
// crawl.
//
// Per rules: code-comments-required, reference-rules-in-comments (ai.knowledge/global).

import { Transport } from '@studio/models/net';

/**
 * The camera's details as typed, field by field (ENG-3796 spec.md §3b /
 * prototype.html "Camera details").
 *
 * Lives here, beside the workflow that collects it, rather than in
 * `onboarding/` — a shared panel reaching up into onboarding for its own types
 * is the wrong way round. `onboarding/wizard/types.ts` re-exports both, and the
 * connection test is what still reads them: the camera's display name and host
 * for its own copy, with every field the add actually uses arriving already
 * resolved from the workflow (see onboarding/add-handoff.ts).
 */
export interface CameraConfig {
  readonly name: string;
  readonly host: string;
  readonly rtspPort: number;
  readonly rtspPath: string;
  readonly viscaPort: number;
  readonly transport?: Transport | undefined;
  readonly username: string;
  readonly password: string;
}

/**
 * What the form opens holding: NOTHING the app has not been told.
 *
 * At this point in the flow the app does not know what camera this is. There
 * is no brand, no model, no discovery result — the user is typing an address
 * by hand. So every box starts empty and the ports show only a PLACEHOLDER of
 * the value that will be used if the box is left alone. A placeholder tells
 * the truth about the default; a filled-in value claims knowledge about the
 * hardware, and this form has none.
 *
 * This replaced a real defect. The grid used to open holding 5678/TCP for the
 * control port, which is a PTZOptics-family endpoint, on a screen that cannot
 * know whether the camera is a PTZOptics. The app's own shipped default is
 * 52381/UDP (`ViscaIpDefaults`), so the form was quietly overriding the
 * product default with a guess about unidentified hardware — the same class of
 * mistake as the retired wizard's invented 5961 port and /live path (ENG-3796,
 * Didier ITB findings 1 and 4).
 *
 * `transport` opens UNSET for the same reason the ports do. It used to open on
 * UDP on the grounds that `ViscaIpDefaults` applies UDP — true, but only when
 * nothing better is known, and for an identified camera the model template IS
 * something better. A pre-selected value cannot be told apart from a chosen one.
 *
 * Zero means "not given". `toCameraUrl` omits an unset port (the RTSP parser
 * then applies 554) and `toControlHint` returns undefined, so the Connect
 * controls step keeps its own defaults untouched.
 */
export const DEFAULT_CAMERA_CONFIG: CameraConfig = {
  name: '',
  host: '',
  rtspPort: 0,
  rtspPath: '',
  viscaPort: 0,
  // UNSET, not UDP. Neither transport is right by default and the evidence cuts
  // both ways on the same camera family: bridge-independent probing got NO
  // response on 52381/UDP and a VISCA ACK with real pan on 5678/TCP, while a
  // PTZOptics Link 4K reached CONNECTION_STATUS_CONNECTED on 1259/UDP straight
  // from its registry template (both measured, ENG-3796).
  //
  // So transport is per-MODEL, and the template is what knows it. Pre-selecting
  // either value here would be indistinguishable from the user having chosen it,
  // and a chosen value must outrank the template — which would mean a silent
  // default quietly overriding the one source that actually knows.
  transport: undefined,
  username: '',
  password: ''
};
